import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROLE_SEEDS } from '@cumpli/shared';
import { Users, TriangleAlert } from 'lucide-react';
import { useUsers, formatDate } from '../lib/hooks';
import { Avatar, Button, Card, EmptyState, Input, PageHeader, Pill, Skeleton, cn } from '../components/ui';

const roleName = (key: string) => ROLE_SEEDS.find((r) => r.key === key)?.name ?? key;

const initials = (name: string) =>
  name.split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase();

export default function UsersList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const { data: users, isLoading, isError, refetch } = useUsers({ search, status });
  // Soft-deleted users are never shown in the console list (the status filter
  // only offers active/deactivated); guard defensively regardless of source.
  const visibleUsers = (users ?? []).filter((u) => u.status !== 'deleted');

  return (
    <div>
      <PageHeader
        title={t('users.title')}
        subtitle="Usuarios del tenant · roles y acceso"
        actions={<Button onClick={() => navigate('/usuarios/nuevo')}>{t('users.create')}</Button>}
      />

      <div className="mb-4 flex gap-3">
        <Input
          placeholder={t('users.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <select
          aria-label={t('users.statusFilter')}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-lg border border-line-strong bg-canvas px-3 text-sm"
        >
          <option value="">{t('users.all')}</option>
          <option value="active">{t('users.active')}</option>
          <option value="deactivated">{t('users.deactivated')}</option>
        </select>
      </div>

      {isLoading ? (
        <Card>
          <div className="grid gap-2.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState
            icon={<TriangleAlert className="h-8 w-8" strokeWidth={1.5} />}
            title="No se pudieron cargar los usuarios"
            hint="Verifica tu conexión e inténtalo de nuevo."
            action={<Button onClick={() => void refetch()}>Reintentar</Button>}
          />
        </Card>
      ) : visibleUsers.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users className="h-8 w-8" strokeWidth={1.5} />}
            title="Sin usuarios"
            hint="No hay usuarios que coincidan con el filtro."
          />
        </Card>
      ) : (
        <div data-tour="users-table">
          {/* mobile: stacked cards (no horizontal scroll) */}
          <div className="grid gap-2.5 sm:hidden">
            {visibleUsers.map((u) => (
              <button
                key={u.id}
                onClick={() => navigate(`/usuarios/${u.id}`)}
                className={cn(
                  'w-full rounded-2xl border border-line bg-panel p-4 text-left shadow-card transition-colors hover:border-ink-faint',
                  u.status === 'deactivated' && 'opacity-70'
                )}
              >
                <div className="flex items-center gap-2.5">
                  <Avatar initials={initials(u.fullName)} />
                  <span className="font-bold">{u.fullName}</span>
                  <Pill tone={u.status === 'active' ? 'green' : 'slate'} className="ml-auto shrink-0">
                    {u.status === 'active' ? t('users.active') : t('users.deactivated')}
                  </Pill>
                </div>
                <div className="mt-1.5 break-all text-sm text-ink-soft">{u.email}</div>
                <div className="mt-1 text-[11px] text-ink-faint">{u.roles.map(roleName).join(', ')}</div>
              </button>
            ))}
          </div>

          {/* desktop: table */}
          <Card className="hidden overflow-x-auto p-0 sm:block">
            <table className="w-full min-w-[44rem] text-sm [&_td]:align-top">
              <thead>
              <tr className="border-b border-line text-left">
                {[t('users.name'), t('users.email'), t('users.roles'), t('users.status'), t('users.lastLogin')].map(
                  (h) => (
                    <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((u) => (
              <tr
                key={u.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/usuarios/${u.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/usuarios/${u.id}`);
                  }
                }}
                className={cn(
                  'cursor-pointer border-b border-line last:border-0 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40',
                  u.status === 'deactivated' && 'text-ink-faint'
                )}
              >
                <td className="px-5 py-3">
                  <span className="flex items-center gap-2.5">
                    <Avatar initials={initials(u.fullName)} />
                    <span className="font-bold">{u.fullName}</span>
                  </span>
                </td>
                <td className="px-5 py-3 text-ink-soft">{u.email}</td>
                <td className="px-5 py-3 text-ink-soft">{u.roles.map(roleName).join(', ')}</td>
                <td className="px-5 py-3">
                  <Pill tone={u.status === 'active' ? 'green' : 'slate'}>
                    {u.status === 'active' ? t('users.active') : t('users.deactivated')}
                  </Pill>
                </td>
                <td className="px-5 py-3 font-mono text-[11px] text-ink-faint">
                  {formatDate(u.lastLoginAt, t('users.never'))}
                </td>
              </tr>
            ))}
            </tbody>
          </table>
          </Card>
        </div>
      )}
    </div>
  );
}
